/**
 * Campfire Effect - creates realistic fire and smoke particle effects
 * Used for smithies, barracks campfires, and other fire sources
 * Features: animated flames, rising smoke, glowing embers
 */

import * as THREE from 'three';

interface FireParticle {
  position: THREE.Vector3;
  velocity: THREE.Vector3;
  age: number;
  maxAge: number;
  size: number;
  type: 'flame' | 'smoke' | 'ember';
}

interface Campfire {
  id: string;
  position: THREE.Vector3;
  intensity: number; // 0-1, affects particle count and size
  particles: FireParticle[];
}

const MAX_PARTICLES_PER_FIRE = 60;
const MAX_CAMPFIRES = 50;

export class CampfireEffect {
  private scene: THREE.Scene;
  private campfires: Map<string, Campfire> = new Map();
  private time: number = 0;

  // Fire particles (instanced)
  private fireGeometry!: THREE.PlaneGeometry;
  private fireMaterial!: THREE.ShaderMaterial;
  private fireInstancedMesh!: THREE.InstancedMesh;
  private fireDummy = new THREE.Object3D();

  // Smoke particles (instanced)
  private smokeGeometry!: THREE.PlaneGeometry;
  private smokeMaterial!: THREE.ShaderMaterial;
  private smokeInstancedMesh!: THREE.InstancedMesh;
  private smokeDummy = new THREE.Object3D();

  // Ember particles (instanced)
  private emberGeometry!: THREE.SphereGeometry;
  private emberMaterial!: THREE.MeshBasicMaterial;
  private emberInstancedMesh!: THREE.InstancedMesh;
  private emberDummy = new THREE.Object3D();

  // Glow meshes for fire (replaces point lights for better performance)
  private fireGlows: Map<string, THREE.Mesh> = new Map();
  private glowGeometry: THREE.SphereGeometry | null = null;
  private glowMaterial: THREE.MeshBasicMaterial | null = null;

  constructor(scene: THREE.Scene) {
    this.scene = scene;
    this.setupFireParticles();
    this.setupSmokeParticles();
    this.setupEmberParticles();
  }

  private setupFireParticles(): void {
    // Billboard plane for fire
    this.fireGeometry = new THREE.PlaneGeometry(0.4, 0.6);

    // Fire shader with animated texture
    this.fireMaterial = new THREE.ShaderMaterial({
      uniforms: {
        uTime: { value: 0 },
      },
      vertexShader: `
        attribute vec3 instanceColor;
        attribute float instanceOpacity;
        varying vec2 vUv;
        varying vec3 vColor;
        varying float vOpacity;

        void main() {
          vUv = uv;
          vColor = instanceColor;
          vOpacity = instanceOpacity;

          // Billboard effect
          vec4 mvPosition = modelViewMatrix * vec4(0.0, 0.0, 0.0, 1.0);
          vec2 scale = vec2(
            length(modelMatrix[0].xyz),
            length(modelMatrix[1].xyz)
          );
          mvPosition.xy += position.xy * scale;
          gl_Position = projectionMatrix * mvPosition;
        }
      `,
      fragmentShader: `
        uniform float uTime;
        varying vec2 vUv;
        varying vec3 vColor;
        varying float vOpacity;

        // Simple noise function
        float hash(vec2 p) {
          return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453);
        }

        float noise(vec2 p) {
          vec2 i = floor(p);
          vec2 f = fract(p);
          f = f * f * (3.0 - 2.0 * f);

          float a = hash(i);
          float b = hash(i + vec2(1.0, 0.0));
          float c = hash(i + vec2(0.0, 1.0));
          float d = hash(i + vec2(1.0, 1.0));

          return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
        }

        void main() {
          vec2 uv = vUv;

          // Create flame shape
          float distFromCenter = length(uv - vec2(0.5, 0.3));
          float flame = 1.0 - smoothstep(0.0, 0.5, distFromCenter);

          // Add noise distortion
          float n = noise(uv * 5.0 + vec2(0.0, uTime * 3.0));
          flame *= 0.8 + n * 0.4;

          // Fade at top
          flame *= 1.0 - smoothstep(0.5, 1.0, uv.y);

          // Fade at edges
          flame *= smoothstep(0.0, 0.2, uv.x) * smoothstep(1.0, 0.8, uv.x);

          // Final color with glow
          vec3 color = vColor;
          float alpha = flame * vOpacity;

          if (alpha < 0.05) discard;

          gl_FragColor = vec4(color, alpha);
        }
      `,
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      side: THREE.DoubleSide,
    });

    // Create instanced mesh
    const maxInstances = MAX_CAMPFIRES * MAX_PARTICLES_PER_FIRE;
    this.fireInstancedMesh = new THREE.InstancedMesh(
      this.fireGeometry,
      this.fireMaterial,
      maxInstances
    );
    this.fireInstancedMesh.count = 0;
    this.fireInstancedMesh.frustumCulled = false;

    // Add instance attributes
    const colors = new Float32Array(maxInstances * 3);
    const opacities = new Float32Array(maxInstances);
    this.fireGeometry.setAttribute('instanceColor', new THREE.InstancedBufferAttribute(colors, 3));
    this.fireGeometry.setAttribute('instanceOpacity', new THREE.InstancedBufferAttribute(opacities, 1));

    this.scene.add(this.fireInstancedMesh);
  }

  private setupSmokeParticles(): void {
    this.smokeGeometry = new THREE.PlaneGeometry(0.5, 0.5);

    this.smokeMaterial = new THREE.ShaderMaterial({
      uniforms: {
        uTime: { value: 0 },
      },
      vertexShader: `
        attribute float instanceOpacity;
        varying vec2 vUv;
        varying float vOpacity;

        void main() {
          vUv = uv;
          vOpacity = instanceOpacity;

          // Billboard
          vec4 mvPosition = modelViewMatrix * vec4(0.0, 0.0, 0.0, 1.0);
          vec2 scale = vec2(
            length(modelMatrix[0].xyz),
            length(modelMatrix[1].xyz)
          );
          mvPosition.xy += position.xy * scale;
          gl_Position = projectionMatrix * mvPosition;
        }
      `,
      fragmentShader: `
        varying vec2 vUv;
        varying float vOpacity;

        void main() {
          float dist = length(vUv - 0.5) * 2.0;
          float alpha = (1.0 - smoothstep(0.0, 1.0, dist)) * vOpacity * 0.3;

          if (alpha < 0.01) discard;

          gl_FragColor = vec4(0.3, 0.3, 0.35, alpha);
        }
      `,
      transparent: true,
      depthWrite: false,
      side: THREE.DoubleSide,
    });

    const maxInstances = MAX_CAMPFIRES * 20;
    this.smokeInstancedMesh = new THREE.InstancedMesh(
      this.smokeGeometry,
      this.smokeMaterial,
      maxInstances
    );
    this.smokeInstancedMesh.count = 0;
    this.smokeInstancedMesh.frustumCulled = false;

    const opacities = new Float32Array(maxInstances);
    this.smokeGeometry.setAttribute('instanceOpacity', new THREE.InstancedBufferAttribute(opacities, 1));

    this.scene.add(this.smokeInstancedMesh);
  }

  private setupEmberParticles(): void {
    this.emberGeometry = new THREE.SphereGeometry(0.04, 4, 4);
    this.emberMaterial = new THREE.MeshBasicMaterial({
      color: 0xffaa33, // Brighter orange-yellow
      transparent: true,
      opacity: 0.95,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });

    const maxInstances = MAX_CAMPFIRES * 10;
    this.emberInstancedMesh = new THREE.InstancedMesh(
      this.emberGeometry,
      this.emberMaterial,
      maxInstances
    );
    this.emberInstancedMesh.count = 0;
    this.emberInstancedMesh.frustumCulled = false;

    this.scene.add(this.emberInstancedMesh);
  }

  addCampfire(id: string, x: number, y: number, z: number, intensity: number = 1.0): void {
    if (this.campfires.has(id) || this.campfires.size >= MAX_CAMPFIRES) return;

    const campfire: Campfire = {
      id,
      position: new THREE.Vector3(x, y, z),
      intensity: Math.max(0.3, Math.min(1.0, intensity)),
      particles: [],
    };

    this.campfires.set(id, campfire);

    // Create shared glow geometry/material if not exists
    if (!this.glowGeometry) {
      this.glowGeometry = new THREE.SphereGeometry(0.3, 8, 6);
      this.glowMaterial = new THREE.MeshBasicMaterial({
        color: 0xff6633,
        transparent: true,
        opacity: 0.6,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
      });
    }

    // Add emissive glow sphere instead of point light (much better performance)
    const glow = new THREE.Mesh(this.glowGeometry, this.glowMaterial!.clone());
    glow.position.set(x, y + 0.3, z);
    glow.scale.setScalar(intensity);
    this.scene.add(glow);
    this.fireGlows.set(id, glow);
  }

  removeCampfire(id: string): void {
    const campfire = this.campfires.get(id);
    if (!campfire) return;

    this.campfires.delete(id);

    // Remove glow mesh
    const glow = this.fireGlows.get(id);
    if (glow) {
      this.scene.remove(glow);
      if (glow.material instanceof THREE.Material) {
        glow.material.dispose();
      }
      this.fireGlows.delete(id);
    }
  }

  private spawnParticle(campfire: Campfire, type: 'flame' | 'smoke' | 'ember'): void {
    if (campfire.particles.length >= MAX_PARTICLES_PER_FIRE) return;

    const pos = campfire.position.clone();
    const vel = new THREE.Vector3();
    let maxAge = 0;
    let size = 1;

    switch (type) {
      case 'flame':
        // Flame particles rise and spread slightly
        pos.x += (Math.random() - 0.5) * 0.2;
        pos.z += (Math.random() - 0.5) * 0.2;
        vel.set(
          (Math.random() - 0.5) * 0.3,
          0.8 + Math.random() * 0.5,
          (Math.random() - 0.5) * 0.3
        );
        maxAge = 0.4 + Math.random() * 0.3;
        size = 0.6 + Math.random() * 0.4;
        break;

      case 'smoke':
        // Smoke rises slowly and spreads
        pos.y += 0.6;
        pos.x += (Math.random() - 0.5) * 0.1;
        pos.z += (Math.random() - 0.5) * 0.1;
        vel.set(
          (Math.random() - 0.5) * 0.2,
          0.3 + Math.random() * 0.2,
          (Math.random() - 0.5) * 0.2
        );
        maxAge = 1.5 + Math.random() * 1.0;
        size = 0.4 + Math.random() * 0.3;
        break;

      case 'ember':
        // Embers shoot upward with some horizontal spread
        pos.x += (Math.random() - 0.5) * 0.15;
        pos.z += (Math.random() - 0.5) * 0.15;
        vel.set(
          (Math.random() - 0.5) * 1.0,
          2.0 + Math.random() * 1.5,
          (Math.random() - 0.5) * 1.0
        );
        maxAge = 0.8 + Math.random() * 0.5;
        size = 0.5 + Math.random() * 0.5;
        break;
    }

    campfire.particles.push({
      position: pos,
      velocity: vel.multiplyScalar(campfire.intensity),
      age: 0,
      maxAge,
      size: size * campfire.intensity,
      type,
    });
  }

  update(deltaTime: number): void {
    this.time += deltaTime;

    // Update shader time
    this.fireMaterial.uniforms.uTime.value = this.time;
    this.smokeMaterial.uniforms.uTime.value = this.time;

    // Track all particles for rendering
    const fireParticles: { particle: FireParticle; campfire: Campfire }[] = [];
    const smokeParticles: { particle: FireParticle; campfire: Campfire }[] = [];
    const emberParticles: { particle: FireParticle; campfire: Campfire }[] = [];

    for (const campfire of this.campfires.values()) {
      // Spawn new particles
      const flameRate = 15 * campfire.intensity;
      const smokeRate = 3 * campfire.intensity;
      const emberRate = 0.5 * campfire.intensity;

      if (Math.random() < flameRate * deltaTime) {
        this.spawnParticle(campfire, 'flame');
      }
      if (Math.random() < smokeRate * deltaTime) {
        this.spawnParticle(campfire, 'smoke');
      }
      if (Math.random() < emberRate * deltaTime) {
        this.spawnParticle(campfire, 'ember');
      }

      // Update existing particles
      for (let i = campfire.particles.length - 1; i >= 0; i--) {
        const p = campfire.particles[i];
        p.age += deltaTime;

        if (p.age >= p.maxAge) {
          campfire.particles.splice(i, 1);
          continue;
        }

        // Update position
        p.position.add(p.velocity.clone().multiplyScalar(deltaTime));

        // Apply forces based on type
        if (p.type === 'ember') {
          p.velocity.y -= 2.0 * deltaTime; // Gravity
          p.velocity.x *= 0.98;
          p.velocity.z *= 0.98;
        } else if (p.type === 'smoke') {
          // Smoke spreads and slows
          p.velocity.x += (Math.random() - 0.5) * 0.1 * deltaTime;
          p.velocity.z += (Math.random() - 0.5) * 0.1 * deltaTime;
          p.velocity.y *= 0.995;
        }

        // Collect for rendering
        if (p.type === 'flame') {
          fireParticles.push({ particle: p, campfire });
        } else if (p.type === 'smoke') {
          smokeParticles.push({ particle: p, campfire });
        } else {
          emberParticles.push({ particle: p, campfire });
        }
      }

      // Update glow flickering (replaces point light flickering)
      const glow = this.fireGlows.get(campfire.id);
      if (glow) {
        const flicker = 0.8 + Math.sin(this.time * 15) * 0.1 + Math.sin(this.time * 23) * 0.1;
        const scale = campfire.intensity * flicker;
        glow.scale.setScalar(scale);
        // Also animate opacity for more dynamic effect
        if (glow.material instanceof THREE.MeshBasicMaterial) {
          glow.material.opacity = 0.4 + flicker * 0.3;
        }
      }
    }

    // Render fire particles
    this.renderFireParticles(fireParticles);
    this.renderSmokeParticles(smokeParticles);
    this.renderEmberParticles(emberParticles);
  }

  private renderFireParticles(particles: { particle: FireParticle; campfire: Campfire }[]): void {
    this.fireInstancedMesh.count = particles.length;

    const colorAttr = this.fireGeometry.getAttribute('instanceColor') as THREE.InstancedBufferAttribute;
    const opacityAttr = this.fireGeometry.getAttribute('instanceOpacity') as THREE.InstancedBufferAttribute;

    for (let i = 0; i < particles.length; i++) {
      const { particle } = particles[i];
      const lifeRatio = particle.age / particle.maxAge;

      // Position
      this.fireDummy.position.copy(particle.position);

      // Scale (grows then shrinks)
      const scale = particle.size * Math.sin(lifeRatio * Math.PI);
      this.fireDummy.scale.setScalar(scale);

      this.fireDummy.updateMatrix();
      this.fireInstancedMesh.setMatrixAt(i, this.fireDummy.matrix);

      // Color: bright yellow core -> orange -> red (enhanced brightness)
      const hue = 0.1 - lifeRatio * 0.08; // Yellow to red
      const saturation = 1.0;
      const lightness = 0.75 - lifeRatio * 0.2; // Brighter to compensate for no point light
      const color = new THREE.Color().setHSL(hue, saturation, lightness);
      colorAttr.setXYZ(i, color.r, color.g, color.b);

      // Opacity (slightly higher for more visible flames)
      const opacity = Math.sin(lifeRatio * Math.PI) * 0.95;
      opacityAttr.setX(i, opacity);
    }

    this.fireInstancedMesh.instanceMatrix.needsUpdate = true;
    colorAttr.needsUpdate = true;
    opacityAttr.needsUpdate = true;
  }

  private renderSmokeParticles(particles: { particle: FireParticle; campfire: Campfire }[]): void {
    this.smokeInstancedMesh.count = particles.length;

    const opacityAttr = this.smokeGeometry.getAttribute('instanceOpacity') as THREE.InstancedBufferAttribute;

    for (let i = 0; i < particles.length; i++) {
      const { particle } = particles[i];
      const lifeRatio = particle.age / particle.maxAge;

      // Position
      this.smokeDummy.position.copy(particle.position);

      // Scale (grows over time)
      const scale = particle.size * (0.5 + lifeRatio * 1.5);
      this.smokeDummy.scale.setScalar(scale);

      this.smokeDummy.updateMatrix();
      this.smokeInstancedMesh.setMatrixAt(i, this.smokeDummy.matrix);

      // Opacity (fades out)
      const opacity = (1.0 - lifeRatio) * 0.6;
      opacityAttr.setX(i, opacity);
    }

    this.smokeInstancedMesh.instanceMatrix.needsUpdate = true;
    opacityAttr.needsUpdate = true;
  }

  private renderEmberParticles(particles: { particle: FireParticle; campfire: Campfire }[]): void {
    this.emberInstancedMesh.count = particles.length;

    for (let i = 0; i < particles.length; i++) {
      const { particle } = particles[i];
      const lifeRatio = particle.age / particle.maxAge;

      // Position
      this.emberDummy.position.copy(particle.position);

      // Scale (fades and shrinks)
      const scale = particle.size * (1.0 - lifeRatio * 0.5);
      this.emberDummy.scale.setScalar(scale);

      this.emberDummy.updateMatrix();
      this.emberInstancedMesh.setMatrixAt(i, this.emberDummy.matrix);
    }

    this.emberInstancedMesh.instanceMatrix.needsUpdate = true;
  }

  getCampfireCount(): number {
    return this.campfires.size;
  }

  clear(): void {
    for (const id of this.campfires.keys()) {
      this.removeCampfire(id);
    }
  }

  dispose(): void {
    this.clear();

    this.scene.remove(this.fireInstancedMesh);
    this.scene.remove(this.smokeInstancedMesh);
    this.scene.remove(this.emberInstancedMesh);

    this.fireGeometry.dispose();
    this.fireMaterial.dispose();
    this.smokeGeometry.dispose();
    this.smokeMaterial.dispose();
    this.emberGeometry.dispose();
    this.emberMaterial.dispose();

    // Dispose shared glow resources
    if (this.glowGeometry) {
      this.glowGeometry.dispose();
    }
    if (this.glowMaterial) {
      this.glowMaterial.dispose();
    }
  }
}
